import { AlarmCriteria } from 'domain/alarm-criteria'
import {
  getCriterionDisplayValue,
  getCriterionTypeLabel,
} from '../config/criterion-config'
import { alarmColors } from '../types/alarm-consts'
import {
  AhcrCriterionTypeEnum,
  AnprCriterionTypeEnum,
} from '../types/alarm.types'

/**
 * Rendering shared by every surface that shows a live SignalR alarm
 * (AlarmSignalR): the full-attention dialog and the recent-alarms feed.
 * Kept in one place so a criterion added to one never silently diverges
 * from the other.
 */

const COLOR_CRITERION_TYPES: string[] = [
  AnprCriterionTypeEnum.COLOR,
  AhcrCriterionTypeEnum.JACKET_COLOR,
  AhcrCriterionTypeEnum.TROUSER_COLOR,
]

export const formatAlarmDate = (value: string): string | null => {
  if (!value) {
    return null
  }

  const parsed = new Date(value)

  if (isNaN(parsed.getTime())) {
    return null
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(parsed)
}

/** Same relative-time shorthand used across the app's live indicators. */
export const formatRelativeAlarmTime = (value: string): string => {
  const parsed = new Date(value)

  if (!value || isNaN(parsed.getTime())) {
    return ''
  }

  const seconds = Math.max(0, (Date.now() - parsed.getTime()) / 1000)

  if (seconds < 60) return 'agora'
  if (seconds < 3600) return `há ${Math.floor(seconds / 60)} min`
  if (seconds < 86400) return `há ${Math.floor(seconds / 3600)} h`
  return formatAlarmDate(value) ?? ''
}

/** Mirrors how criteria are rendered in the alarms feature: a plate gets the
 *  Mercosul component (handled by the caller), colors get a swatch,
 *  everything else its display value. */
export const renderCriterionValue = (criteria: AlarmCriteria) => {
  if (COLOR_CRITERION_TYPES.includes(criteria.type)) {
    const color = alarmColors.find((option) => option.value === criteria.value)

    return (
      <span className='alarm-dialog__color'>
        <span
          className='alarm-dialog__swatch'
          style={{ backgroundColor: color?.hex ?? '#000' }}
        />
        {color?.name ?? criteria.value}
      </span>
    )
  }

  return getCriterionDisplayValue(criteria.value)
}

export { getCriterionTypeLabel }
